import { EAxiosResponseCode, ESystemType, getHttpErrorMessage, Http } from '@core/api';
import { getHostbaseUrl } from '@core/tools';
import { IAlipayAccountReopenRequest, IAlipayBindParam, IAlipayChargeParam, IAlipayCompanyListItem, IAlipayCompanyListParam, IAlipayWithdrawParam, IPreTopUpListItem, IPreTopUpParam, IPreTopUpThresholdParam, IPreTopUpTransaction, IPreTopUpTransactionParam } from '../../types/alipay';

const hostUrl = getHostbaseUrl();

/**
 * 众包灵工 - 结算订单列表查询
 */
export async function queryAlipayCompanies(params: IAlipayCompanyListParam) {
  let total = 0;
  let data = [] as IAlipayCompanyListItem[];

  try {
    const { code, context, message } = await Http.get<{ data: IAlipayCompanyListItem[]; total: number }>(
      `${hostUrl}/pay/alipay-account/page`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IAlipayCompanyListItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;

      data.forEach((item) => {
      });
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 预充值企业列表查询
 */
export async function queryPreTopUpList(params: IPreTopUpParam, term: 'agent' | 'company', systemType: ESystemType) {
  let total = 0;
  let data = [] as IPreTopUpListItem[];

  try {
    const { code, context, message } = await Http.post<{ data: IPreTopUpListItem[]; total: number }>(
      `${hostUrl}/pay/advance-account/page-query/${term === 'agent' ? 'agent' : 'company'}`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IPreTopUpListItem[]; total: number };

      data = res.data || [];
      total = res.total || 0;

      data.forEach((item) => {
        item.showPreTopUp = !(systemType === ESystemType.SAAS && term === 'company');
      });
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 查询企业预充值账户交易列表
 */
export async function queryPreTopUpTransactions(params: IPreTopUpTransactionParam, isAgent: boolean) {
  let total = 0;
  let data = [] as IPreTopUpTransaction[];

  try {
    const { code, context, message } = await Http.post<{ data: IPreTopUpTransaction[]; total: number }>(
      `${hostUrl}/pay/advance-account/transaction/${isAgent ? 'agent' : 'company'}/page-query`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      const res = context || {} as { data: IPreTopUpTransaction[]; total: number };

      data = res.data || [];
      total = res.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    data,
    total,
  };
}

/**
 * 预充值企业交易导出
 */
export async function exportPreTopUpTransactions(param: IPreTopUpTransactionParam, isAgent: boolean) {
  let buffer = null as Blob | null;
  let fileName = '';

  try {
    const { code, context, message, headers } = await Http.get<Blob>(
      `${hostUrl}/pay/advance-account/transaction/${isAgent ? 'agent' : 'company'}/export`,
      param,
      { responseType: 'blob' },
    );

    if (headers['content-disposition']) {
      const match = headers['content-disposition'].match(/filename=(.+);?/);

      if (match && match[1]) {
        fileName = decodeURIComponent(match[1].trim().replace(/"/g, ''));
      }
    }

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    buffer,
    fileName,
  };
}

/**
 * 预充值企业阈值配置/充值
 */
export async function saveThresholdOrCharge(params: IPreTopUpThresholdParam, isAgent = false) {
  const { code, message } = await Http.post<void>(`${hostUrl}/pay/advance-account/modify/${isAgent ? 'agent' : 'company'}`, params);

  if (code !== EAxiosResponseCode.Succeed) {
    throw new Error(message || '服务器异常，请稍后再试~');
  }
}

/**
 * 众包灵工 - 绑定支付宝
 */
export async function queryAlipaySignUrl(params: IAlipayBindParam) {
  const { code, context, message } = await Http.post<string>(`${hostUrl}/pay/alipay-account/sign-url`, params);

  if (code === EAxiosResponseCode.Succeed) {
    return context || '';
  } else {
    throw new Error(message || '服务器异常，请稍后再试~');
  }
}

/**
 * 众包灵工 - 支付宝提现
 */
export async function doAlipayWithdraw(params: IAlipayWithdrawParam) {
  const { code, message } = await Http.post<void>(`${hostUrl}/pay/alipay-account/withdraw`, params);

  if (code !== EAxiosResponseCode.Succeed) {
    throw new Error(message || '服务器异常，请稍后再试~');
  }
}

/**
 * 众包灵工 - 支付宝
 */
export async function doAlipayCharge(params: IAlipayChargeParam) {
  const { code, context, message } = await Http.post<string>(`${hostUrl}/pay/alipay-account/recharge`, params);

  if (code !== EAxiosResponseCode.Succeed) {
    throw new Error(message || '服务器异常，请稍后再试~');
  }

  return context || '';
}

/**
 * 众包灵工 - 支付宝进件
 */
export async function alipayAccounTonboarding(params: any) {
  const { code, context, message } = await Http.post<string>(`${hostUrl}/pay/alipay-account/onboarding`, params);

  if (code !== EAxiosResponseCode.Succeed) {
    throw new Error(message || '服务器异常，请稍后再试~');
  }

  return context || '';
}

/**
 * 支付宝管理 -支付宝进件详细信息
 */
export async function alipayAccounTonboardingDetail(param: {accountId: number}) {
  let data = {} as any;

  try {
    const { code, context, message } = await Http.post<any>(
      `${hostUrl}/pay/alipay-account/onboarding-detail`,
      { ...param },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as any;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 众包灵工 - 撤销支付宝进件
 */
export async function revokeAlipayAccounTonboarding(params: any) {
  const { code, context, message } = await Http.post<string>(`${hostUrl}/pay/alipay-account/revoke`, params);

  if (code !== EAxiosResponseCode.Succeed) {
    throw new Error(message || '服务器异常，请稍后再试~');
  }

  return context || '';
}

/**
 * 记账本重新开通
 */
export async function reopenAlipayAccount(params: IAlipayAccountReopenRequest) {
  const { code, message } = await Http.post<void>(`${hostUrl}/pay/alipay-account/reopen`, params);

  if (code !== EAxiosResponseCode.Succeed) {
    throw new Error(message || '服务器异常，请稍后再试~');
  }
}
